import { useState } from "react";

import { Button } from "@app/components/ui/button";
import { Slider } from "@app/components/ui/slider";
import { Trans } from "@lingui/macro";
import dayjs from "dayjs";
import { RotateCcw } from "lucide-react";

interface PatientTemporalSliderProps {
  minDate: string;
  maxDate: string;
  value: string | null;
  onChange: (iso: string) => void;
  onReset: () => void;
}

const ONE_DAY_MS = 86_400_000;

function isoDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

export function PatientTemporalSlider({
  minDate,
  maxDate,
  value,
  onChange,
  onReset,
}: PatientTemporalSliderProps) {
  /* Where the thumb is while it is dragged. The dossier is read where the reader lets go, not at
     every day the thumb crosses: a drag across five years sent hundreds of `/state` requests, each
     re-reading the whole journal. A key press is a release, so the arrows still step day by day. */
  const [draggedMs, setDraggedMs] = useState<number | null>(null);
  const minMs = new Date(minDate).getTime();
  const maxMs = new Date(maxDate).getTime();
  if (!Number.isFinite(minMs) || !Number.isFinite(maxMs) || minMs >= maxMs) {
    return null;
  }
  const currentMs = draggedMs ?? (value ? new Date(value).getTime() : maxMs);
  const shown = draggedMs !== null ? isoDay(draggedMs) : value;
  const isReplay = shown !== null && shown < maxDate.slice(0, 10);

  return (
    <div
      data-tour="patient-timeline"
      className="flex items-center gap-3 min-w-0 min-h-8 flex-1"
    >
      <span className="text-xs font-medium text-slate-600 whitespace-nowrap">
        {isReplay ? (
          <Trans>État au {dayjs(shown).format("DD MMM YYYY")}</Trans>
        ) : (
          <Trans>Aujourd'hui</Trans>
        )}
      </span>
      {/* The bounds hang below the track, out of the flow, so the track — not the track and its
          bounds together — is what centers on the header's row of labels and buttons. */}
      <div className="relative flex-1 min-w-[180px] max-w-[350px]">
        <Slider
          min={minMs}
          max={maxMs}
          step={ONE_DAY_MS}
          value={[currentMs]}
          onValueChange={(vals: number[]) => setDraggedMs(vals[0])}
          onValueCommit={(vals: number[]) => {
            setDraggedMs(null);
            onChange(isoDay(vals[0]));
          }}
        />
        <div className="absolute inset-x-0 top-full mt-1 flex justify-between text-3xs text-slate-500">
          <span>{dayjs(minDate).format("MMM YYYY")}</span>
          <span>{dayjs(maxDate).format("MMM YYYY")}</span>
        </div>
      </div>
      {isReplay && (
        <Button
          size="sm"
          variant="outline"
          onClick={onReset}
          className="border-slate-200 hover:border-primary/50 h-8 text-xs"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1" />
          <Trans>Présent</Trans>
        </Button>
      )}
    </div>
  );
}
